/**
 * @Description 模型类
 * @Author iCEA
 * @Date 2020-10-29
 * @Version 1.0
 */

// import { OBJLoader2 } from 'three/examples/jsm/loaders/OBJLoader2'
import { EventEmitter } from 'events/events'
import { DRACOLoader } from './Loader/DRACOLoader'
import { GLTFLoader } from './Loader/GLTFLoader'
import { BufferGeometryUtils } from 'three/examples/jsm/utils/BufferGeometryUtils'
import * as THREE from 'three'
import { GodMaterial } from './ModelMaterial_offscreen'

class Model {
  /**
   * 模型类构造函数
   * @param {string} moderUrl 模型文件路径
   * @returns {Model} 模型类单例
   */
  constructor (moderUrl) {
    /**
     * @type {string} 模型文件url
     */
    this.modelUrl = moderUrl
    /**
     * @type {string} draco解码文件url
     */
    this.dracoDecoderUrl = `${process.env.VUE_APP_RESOURCE_ACCESS_URL}/public/gltf/`
    /**
     * @type {THREE.Object3D[]} 模型各个part的列表，射线检测需要
     */
    this.objects = []

    /**
     * @type {THREE.Object3D[]} 渲染的模型列表，用于替换材质
     */
    this.renderObjs = []
  }

  /**
   * 初始化模型，加载模型，包围盒
   * @param {EventEmitter} emitter 事件触发器，用于触发进度条
   * @param {THREE.Scene} scene 模型的所放置的根路径
   * @param {THREE.Material} moedelMat 模型材质
   * @param {THREE.Material} godMat 上帝材质
   */
  async initModel (emitter, scene, moedelMat, godMat) {
    // 模型加载
    // const gltfLoader = new OBJLoader2()
    const gltfLoader = new GLTFLoader()
    const dracoLoader = new DRACOLoader()
    dracoLoader.setDecoderPath(this.dracoDecoderUrl)
    dracoLoader.setDecoderConfig({ type: 'wasm' }) // 找到解码.js文件
    dracoLoader.preload() // 预加载
    gltfLoader.setDRACOLoader(dracoLoader) // gltfLoader设置draco解码器
    // 返回一个Promise()
    return gltfLoader.loadAsync(this.modelUrl, (e) => {
      emitter.emit('loadingTo', Math.round(10 + e.loaded * 70 / e.total))
    }).then((obj) => { // 获取2.03M的模型文件加载
      const cGeos = []

      // 寻找根节点
      let group = obj.scene
      while (group.children[0] && group.children[0] instanceof THREE.Group) {
        group = group.children[0]
      }
      emitter.emit('loadingTo', 88)
      let i = 0
      const len = group.children.length

      // 记录材质
      const map = {}
      const geos = {}

      // 加载完obj文件是一个场景组，遍历它的子元素，赋值纹理并且更新面和点的法向量
      group.children.forEach((child) => {
        let mat = null
        let geo = []

        // child = child.children[0]
        if (map[child.material.uuid]) {
          mat = map[child.material.uuid]
          geo = geos[child.material.uuid]
        } else {
          mat = new THREE.MeshBasicMaterial({ map: child.material.map })
          mat.minFilter = THREE.LinearFilter
          map[child.material.uuid] = mat
          geos[child.material.uuid] = geo
        }

        // 记录子：材质
        child.material = mat
        godMat.materials.push(child.material)
        // 网格
        child.geometry.computeFaceNormals()
        child.geometry.computeVertexNormals()
        child.geometry.computeBoundingBox()
        cGeos.push(child.geometry)
        geo.push(child.geometry)

        this.objects.push(child) // 分组合并
        if (i++ % 20 === 0) {
          emitter.emit('loadingTo', Math.round(81 + i * 18 / len))
        }
      })

      for (const obj of this.objects) {
        scene.add(obj)
      }
      this.renderObjs = this.objects

      // 创建cube
      const modelGeo = BufferGeometryUtils.mergeBufferGeometries(cGeos)
      modelGeo.computeBoundingBox()
      const aabb = new THREE.Box3()
      aabb.union(modelGeo.boundingBox)
      modelGeo.dispose()
      const aabbExtend = aabb.max.clone().sub(aabb.min).addScalar(10)
      const center = aabbExtend.clone().multiplyScalar(0.5).add(aabb.min)
      this.cube = new THREE.Mesh(new THREE.BoxGeometry(aabbExtend.x, aabbExtend.y, aabbExtend.z),
        moedelMat)
      this.cube.position.copy(center)
      this.cube.name = 'cube'
      scene.add(this.cube)
    }, reason => console.error(reason))
  }

  get center () {
    return this.cube.position.clone()
  }

  /**
   * 隐藏盒
   */
  removeCube () {
    this.cube.visible = false
  }

  /**
   * 显示盒
   */
  addCube () {
    this.cube.visible = true
  }
}

export {
  Model
}
